<template>
  <div class="home detail-actions-page">
    <div class="px-8 py-4">
      <!-- Header -->
      <div class="invoice-details-header mb-4">
        <button type="button" class="btn invoice-back-button" :aria-label="$t('back')" @click="$router.go(-1)">
          <AppIcon name="bi-arrow-left" class="bi bi-arrow-left" aria-hidden="true" />
          <span>{{$t('back')}}</span>
        </button>
        <div class="invoice-title-row d-flex flex-column flex-lg-row justify-content-between align-items-start align-items-lg-center gap-3">
          <h3 class="invoice-reference mb-0">{{ quote.reference }}</h3>
          <div class="invoice-actions" role="toolbar" :aria-label="$t('actions')">
            <button
              v-if="quote.status != 'draft'"
              type="button"
              class="btn invoice-action-button invoice-action-button--primary"
              @click="ViewInvoice()"
            >
              <AppIcon name="bi-file-earmark-pdf" class="bi bi-file-earmark-pdf" aria-hidden="true" />
              <span>{{$t('view')}}</span>
            </button>
            <button
              v-if="helper.checkAccess('edit_quote') != -1"
              type="button"
              class="btn invoice-action-button invoice-action-button--secondary"
              @click="editRow()"
            >
              <AppIcon name="bi-pencil" class="bi bi-pencil" aria-hidden="true" />
              <span>{{$t('edit')}}</span>
            </button>
            <button
              v-if="quote.status != 'draft' && helper.checkAccess('create_quote') != -1"
              type="button"
              class="btn invoice-action-button invoice-action-button--secondary"
              @click="duplicateRow()"
            >
              <AppIcon name="bi-copy" class="bi bi-copy" aria-hidden="true" />
              <span>{{$t('duplicate')}}</span>
            </button>
            <div
              v-if="quote.status != 'draft' && (helper.checkAccess('create_orders') > 0 || helper.checkAccess('create_deliveries') > 0 || helper.checkAccess('create_invoices') > 0)"
              class="btn-group invoice-action-group"
            >
              <button
                id="quoteTransferButton"
                class="btn invoice-action-button invoice-action-button--secondary dropdown-toggle"
                type="button"
                data-bs-toggle="dropdown"
                aria-haspopup="true"
                aria-expanded="false"
              >
                <AppIcon name="bi-arrow-left-right" class="bi bi-arrow-left-right" aria-hidden="true" />
                <span>{{$t('transfert')}}</span>
              </button>
              <ul class="dropdown-menu dropdown-menu-end invoice-actions-menu" aria-labelledby="quoteTransferButton">
                <li v-if="helper.checkAccess('create_orders') > 0">
                  <button type="button" class="dropdown-item" @click="quoteAccepted()">
                    <AppIcon name="bi-cart" class="bi bi-cart" aria-hidden="true" />
                    <span>{{$t('order')}}</span>
                  </button>
                </li>
                <li v-if="helper.checkAccess('create_deliveries') > 0">
                  <button type="button" class="dropdown-item" @click="transferDelivery()">
                    <AppIcon name="bi-truck" class="bi bi-truck" aria-hidden="true" />
                    <span>{{$t('delivery')}}</span>
                  </button>
                </li>
                <li v-if="helper.checkAccess('create_invoices') > 0">
                  <button type="button" class="dropdown-item" @click="transferInvoice()">
                    <AppIcon name="bi-receipt" class="bi bi-receipt" aria-hidden="true" />
                    <span>{{$t('invoice')}}</span>
                  </button>
                </li>
              </ul>
            </div>
            <button
              v-if="quote.status != 'draft'"
              type="button"
              class="btn invoice-action-button invoice-action-button--secondary"
              @click="openSendWithMsg()"
            >
              <AppIcon name="bi-envelope-paper" class="bi bi-envelope-paper" aria-hidden="true" />
              <span>{{$t('send_email')}}</span>
            </button>
            <div
              v-if="quote.status != 'draft' || helper.checkAccess('delete_quote') != -1"
              class="btn-group invoice-action-group"
            >
              <button
                id="quoteMoreActionsButton"
                class="btn invoice-action-button invoice-action-button--secondary invoice-action-button--icon dropdown-toggle"
                type="button"
                data-bs-toggle="dropdown"
                aria-haspopup="true"
                aria-expanded="false"
                :aria-label="$t('more')"
                :title="$t('more')"
              >
                <AppIcon name="bi-three-dots" class="bi bi-three-dots" aria-hidden="true" />
              </button>
              <ul class="dropdown-menu dropdown-menu-end invoice-actions-menu" aria-labelledby="quoteMoreActionsButton">
                <li v-if="quote.status != 'draft'">
                  <button type="button" class="dropdown-item" @click="toggleReorder">
                    <AppIcon name="bi-arrow-down-up" class="bi bi-arrow-down-up" aria-hidden="true" />
                    <span>{{$t('reorder')}}</span>
                  </button>
                </li>
                <li v-if="quote.status != 'draft' && helper.checkAccess('delete_quote') != -1"><hr class="dropdown-divider"></li>
                <li v-if="helper.checkAccess('delete_quote') != -1">
                  <button type="button" class="dropdown-item invoice-menu-action--danger" @click="deleteRow()">
                    <AppIcon name="bi-trash" class="bi bi-trash" aria-hidden="true" />
                    <span>{{$t('delete')}}</span>
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="text-center" style="padding:2rem">
        <div class="spinner-border" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>

      <!-- Quote Info -->
      <div v-else-if="quote && quote.id">
        <!-- Tabs -->
        <ul class="nav nav-tabs detail-page-tabs mb-4">
          <li class="nav-item">
            <a class="nav-link" :class="{active: step === 'overview'}" @click="step = 'overview'">{{$t('overview')}}</a>
          </li>
          <li class="nav-item" v-if="helper.checkAccess('view_attachments') != -1">
            <a class="nav-link" :class="{active: step === 'attachments'}" @click="step = 'attachments'">{{$t('attachments')}}</a>
          </li>
          <li class="nav-item">
            <a class="nav-link" :class="{active: step === 'history'}" @click="step = 'history'">{{$t('history')}}</a>
          </li>
        </ul>

        <!-- Tab Content -->
        <!-- Overview Tab -->
        <div v-if="step === 'overview'">
          <div class="row">
            <!-- Left Column: PDF and line items -->
            <div class="col-md-8">
              <div class="card mb-4 quote-pdf-card">
                <div class="card-body p-0">
                  <invoice-template-modal inline :data="quote" type="quote" />
                </div>
              </div>

              <!-- Products Card -->
              <div v-if="reorderMode" class="card mb-4">
                <div class="card-header bg-white">
                  <div class="d-flex justify-content-between align-items-center">
                    <h6 class="mb-0"><AppIcon name="bi-box-seam" class="bi bi-box-seam me-2" />{{$t('products')}}</h6>
                    <div class="product-reorder-actions">
                        <div class="d-flex flex-wrap gap-2" role="group" :aria-label="$t('reorder')">
                            <button type="button" class="btn product-action-button product-action-button--save" @click="saveOrder">
                                <AppIcon name="bi-check-lg" class="bi bi-check-lg" aria-hidden="true" />
                                {{$t('save')}}
                            </button>
                            <button type="button" class="btn product-action-button" @click="toggleReorder">
                                <AppIcon name="bi-x-lg" class="bi bi-x-lg" aria-hidden="true" />
                                {{$t('cancel')}}
                            </button>
                        </div>
                    </div>
                  </div>
                </div>
                <div class="card-body table-responsive">
                  <table class="table mb-0 table-striped small">
                    <thead>
                      <tr class="text-secondary">
                        <th>{{$t('product_label')}}</th>
                        <th>{{$t('price')}}</th>
                        <th>{{$t('quantity')}}</th>
                        <th>{{$t('vat')}}</th>
                        <th>{{$t('sub_total')}}</th>
                        <th>{{$t('total')}}</th>
                        <th v-if="reorderMode" style="width: 100px;">{{$t('actions')}}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(product, index) in quote.products" :key="product.id">
                        <td><div class="quote-product-rich-text" v-html="sanitizeRichText(product.product_label)"></div></td>
                        <td>{{formatMoney(product.price)}}</td>
                        <td>{{product.quantity}}</td>
                        <td>{{product.vat}} %</td>
                        <td>{{formatMoney(product.sub_total)}}</td>
                        <td>{{formatMoney(product.total)}}</td>
                        <td v-if="reorderMode">
                            <div class="d-flex gap-1">
                                <button
                                  type="button"
                                  class="btn product-move-button"
                                  :aria-label="$t('reorder') + ' ↑'"
                                  :title="$t('reorder') + ' ↑'"
                                  :disabled="index === 0"
                                  @click="moveUp(index)"
                                >
                                  <AppIcon name="bi-arrow-up" class="bi bi-arrow-up" aria-hidden="true" />
                                </button>
                                <button
                                  type="button"
                                  class="btn product-move-button"
                                  :aria-label="$t('reorder') + ' ↓'"
                                  :title="$t('reorder') + ' ↓'"
                                  :disabled="index === quote.products.length - 1"
                                  @click="moveDown(index)"
                                >
                                  <AppIcon name="bi-arrow-down" class="bi bi-arrow-down" aria-hidden="true" />
                                </button>
                            </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <!-- Notes Card -->
              <div v-if="quote.notes" class="card">
                <div class="card-header bg-white">
                  <h6 class="mb-0"><AppIcon name="bi-sticky" class="bi bi-sticky me-2" />{{$t('notes')}}</h6>
                </div>
                <div class="card-body">
                  <p class="mb-0">{{ quote.notes }}</p>
                </div>
              </div>
            </div>

            <!-- Right Column: Quote and client info -->
            <div class="col-md-4">
              <div class="card mb-4">
                <div class="card-header bg-white">
                  <h6 class="mb-0"><AppIcon name="bi-file-text" class="bi bi-file-text me-2" />{{$t('quote')}} {{$t('information')}}</h6>
                </div>
                <div class="card-body">
                  <div class="row">
                    <div class="col-6 mb-3">
                      <small class="text-muted d-block">{{$t('reference')}}</small>
                      <strong>{{quote.reference}}</strong>
                    </div>
                    <div class="col-6 mb-3">
                      <small class="text-muted d-block">{{$t('status')}}</small>
                      <div
                        v-if="helper.checkAccess('edit_quote') != -1 && !['draft', 'canceled'].includes(quote.status)"
                        class="dropdown"
                      >
                        <button
                          id="quote-details-status"
                          type="button"
                          class="badge border-0 dropdown-toggle"
                          :class="getQuoteStatusBadgeClass(quote.status)"
                          data-bs-toggle="dropdown"
                          aria-expanded="false"
                          :disabled="isQuoteStatusDropdownDisabled(quote)"
                        >
                          <span v-if="quoteStatusUpdatingId === quote.id" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                          {{ $t(quote.status) }}
                        </button>
                        <ul class="dropdown-menu shadow" aria-labelledby="quote-details-status">
                          <li v-for="status in getQuoteStatusOptions(quote)" :key="status">
                            <button
                              type="button"
                              class="dropdown-item d-flex align-items-center justify-content-between gap-3"
                              :class="{ active: quote.status === status }"
                              :disabled="quote.status === status"
                              @click="changeQuoteStatus(quote, status)"
                            >
                              <span>{{ $t(status) }}</span>
                              <AppIcon name="bi-check-lg" v-if="quote.status === status" class="bi bi-check-lg" />
                            </button>
                          </li>
                        </ul>
                      </div>
                      <span v-else class="badge" :class="getQuoteStatusBadgeClass(quote.status)">
                        {{ $t(quote.status) }}
                      </span>
                    </div>
                    <div class="col-6 mb-3">
                      <small class="text-muted d-block">{{$t('order_date')}}</small>
                      <strong>{{formatDate(quote.date)}}</strong>
                    </div>
                    <div class="col-6 mb-3">
                      <small class="text-muted d-block">{{$t('created_by')}}</small>
                      <strong>{{quote.user ? quote.user.name : '-'}}</strong>
                    </div>
                    <div class="col-6 mb-3">
                      <small class="text-muted d-block">{{$t('created_at')}}</small>
                      <strong>{{formatDate(quote.created_at)}}</strong>
                    </div>
                    <div class="col-6 mb-3">
                      <small class="text-muted d-block">{{$t('updated_at')}}</small>
                      <strong>{{formatDate(quote.updated_at)}}</strong>
                    </div>
                  </div>
                </div>
              </div>

              <div class="card mb-4">
                <div class="card-header bg-white">
                  <h6 class="mb-0"><AppIcon name="bi-person" class="bi bi-person me-2" />{{$t('client')}}</h6>
                </div>
                <div class="card-body">
                  <div v-if="!quote.client" class="text-muted">{{$t('no_client_found')}}</div>
                  <div v-else>
                    <div class="mb-3">
                      <small class="text-muted d-block">{{$t('reference')}}</small>
                      <strong>{{quote?.client?.reference}}</strong>
                    </div>
                    <div class="mb-3">
                      <small class="text-muted d-block">{{$t('name')}}</small>
                      <strong>{{quote?.client?.name}}</strong>
                    </div>
                    <div class="mb-3">
                      <small class="text-muted d-block">{{$t('phone')}}</small>
                      <strong>{{quote?.client?.phone || '-'}}</strong>
                    </div>
                    <div>
                      <small class="text-muted d-block">{{$t('email')}}</small>
                      <strong>{{quote?.client?.email || '-'}}</strong>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Tot  als Card -->
              <div class="card">
                <div class="card-header bg-white">
                  <h6 class="mb-0"><AppIcon name="bi-calculator" class="bi bi-calculator me-2" />{{$t('totals')}}</h6>
                </div>
                <div class="card-body">
                  <div class="mb-3">
                    <small class="text-muted d-block">{{$t('sub_total')}}</small>
                    <h5 class="mb-0 text-muted">{{formatMoney(sub_total)}}</h5>
                  </div>
                  <div class="mb-3" v-if="quote.total_discount > 0">
                    <small class="text-muted d-block">{{$t('total_discount')}}</small>
                    <h5 class="mb-0 text-muted">-{{formatMoney(quote.total_discount)}}</h5>
                  </div>
                  <div class="mb-3">
                    <small class="text-muted d-block">{{$t('total_tax')}}</small>
                    <h5 class="mb-0 text-muted">{{formatMoney(quote.total_vat)}}</h5>
                  </div>  
                  <div>
                    <small class="text-muted d-block">{{$t('total')}}</small>
                    <h5 class="text mb-0">{{formatMoney(total)}}</h5>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Other Tabs -->
        <div v-if="step === 'attachments' && helper.checkAccess('view_attachments') != -1">
          <attachments v-if="quote && quote.id" :entity_id="quote.id" entity_type="quote" />
        </div>

        <div v-if="step === 'history'">
          <document-history
            :history="history"
            :loading="loadingHistory"
            :pagination="historyPagination"
            @pageChange="changeHistoryPage"
          />
        </div>
      </div>
    </div>


        <!-- quote modal -->
        <invoice-template-modal 
            v-if="invoiceModalisOpen" 
            @modalClosed="invoiceModalisOpen = false" 
            modalId="invoice-quote-modal-22" 
            :isModalOpen="invoiceModalisOpen"  
            :data="invoiceData" 
            type="quote"
            :showSendForm="showSendForm"
            :defaultEmail="quote?.client?.email || ''"
            :emailSent="emailSent"
            @sendEmail="sendEmailFromPreview">
        </invoice-template-modal>
        <!-- quote modal -->

            <!-- Edit Modal -->
        <add-edit-quote-modal 
            v-if="editModalisOpen" 
            @modalClosed="editModalisOpen = false" 
            modalId="edit-quote-modal-01" 
            :isModalOpen="editModalisOpen"  
            :data="quote" 
            action="update" 
        ></add-edit-quote-modal>
        <!-- Edit modal -->

        <!-- Duplicate Modal -->
        <add-edit-quote-modal 
            v-if="createModalisOpen" 
            @modalClosed="createModalisOpen = false" 
            modalId="create-quote-modal-01" 
            :isModalOpen="createModalisOpen"  
            :data="quoteToDuplicate" 
            action="create" 
        ></add-edit-quote-modal>
        <!-- Duplicate modal -->
            
        <add-client 
            v-if="createClientState"
            :state="createClientState" 
            @createClient="createClient"
            />

        <!-- create Modal -->
        <add-edit-invoice-modal 
            v-if="createInvoiceModalState" 
            @modalClosed="createInvoiceModalState = false"
            modalId="create-invoice-modal-01" 
            :isModalOpen="createInvoiceModalState" 
            :data="quote" 
            action="transfer"
        ></add-edit-invoice-modal>
        <!-- create modal -->


        <!-- create Modal -->
        <add-edit-delivery-modal 
            v-if="createDeliveryModalState" 
            @modalClosed="createDeliveryModalState = false"
            modalId="create-delivery-modal-01" 
            :isModalOpen="createDeliveryModalState" 
            :data="quote" 
            action="transfer"
        ></add-edit-delivery-modal>
        <!-- create modal -->


        <!-- create Modal -->
        <add-edit-order-modal 
            v-if="createOrderModalState" 
            @modalClosed="createOrderModalState = false"
            modalId="create-order-modal-01" 
            :isModalOpen="createOrderModalState" 
            :data="quote" 
            action="transfer"
        ></add-edit-order-modal>
        <!-- create modal -->

    </div>

</template>

<script>
// @ is an alias to /src
import helper from '../../helper'
import AddClient from '../../components/AddClient.vue'
import moment from 'moment'
import AddEditQuoteModal from '../../components/quote/AddEditQuoteModal'
import AddEditInvoiceModal from '../../components/invoice/AddEditInvoiceModal'
import AddEditDeliveryModal from '../../components/delivery/AddEditDeliveryModal'
import AddEditOrderModal from '../../components/order/AddEditOrderModal'
import InvoiceTemplateModal from '../../components/invoiceLayout/InvoiceTemplateModal';
import attachments from '../../components/Attachment'
import DocumentHistory from '../../components/DocumentHistory.vue'

export default {
  name: 'InvoiceDetails',
  components :{
    AddClient,
    InvoiceTemplateModal,
    AddEditQuoteModal,
    attachments,
    AddEditInvoiceModal,
    AddEditDeliveryModal,
    AddEditOrderModal,
    DocumentHistory
  },
  data(){
    return {
        loading : false,
        loadingHistory : false,
        quote : {},
        history : [],
        historyPagination : {
            count: 0,
            per_page: 10,
            current_page: 1,
            total_pages: 1
        },
        amount_paid : 0,
        step : 'overview',
        total_purchases : 0,
        total_quote : 0,
        invoiceModalisOpen : false,
        invoiceModal : null,
        editModal : null,
        editModalisOpen : false,
        createClientState : false,
        createInvoiceModalState : false,
        createDeliveryModalState :false,
        createOrderModalState : false,
        emailForm : {
            email : ''
        },
        sendEmailModal : null,

        showSendForm: false,
        emailSent: false,
        reorderMode: false,
        originalProducts: [],
        createModalisOpen: false,
        quoteToDuplicate: null,
        quoteStatusUpdatingId: null,
        quoteStatusOptions: ['pending', 'confirmed', 'canceled']
    }
  },
  async created(){
    if(!this.$route.params || !this.$route.params.id) return this.$route.push('/deliveries')
    await this.getQuote(this.$route.params.id)
  },
  mounted(){
    window.mixpanel.track("quote_details");

    //check permissions
    if(helper.checkAccess('view_quotes') == -1) return this.$router.push({path : '/'})
    if(helper.checkAccess('view_quotes') == -2) return this.$router.push({path : '/subscribe'})

    // Initialize email modal

  },
  methods : {
        ViewInvoice(){
            this.invoiceData = this.quote 
            this.showSendForm = false
            this.emailSent = false
            this.invoiceModalisOpen = true;
        },
        openSendWithMsg(){
            this.invoiceData = this.quote
            this.showSendForm = true
            this.emailSent = false
            this.invoiceModalisOpen = true
        },
        async sharableLink() {
            const response = await helper.customPost(`${process.env.VUE_APP_API}/external-documents`, {
                document_id: this.quote.id,
                document_type: 'quote'
            });

            const link = response.data.link;

            try {
                await navigator.clipboard.writeText(link); // Copy to clipboard
                this.$toast.open({
                    message: this.$t('Copied'),
                    type: 'success',
                    position: 'bottom'
                });
            } catch (err) {
                this.$toast.open({
                    message: err.message,
                    type: 'error',
                    position: 'bottom'
                });
            }
        },
        async getQuote(id){
        try{
            this.loading = true
            const response = await helper.customGet(`${process.env.VUE_APP_API}/quotes/${id}`)
            this.quote = response.data
            this.loading = false
            
        }catch(error){
            this.loading = false
        }
        },
        getQuoteStatusBadgeClass(status){
            return {
                'bg-info': status === 'draft',
                'bg-success': status === 'confirmed',
                'bg-warning': status === 'partially_paid',
                'bg-danger': status === 'canceled',
                'bg-secondary': status === 'pending'
            }
        },
        sanitizeRichText(value){
            return helper.sanitizeRichText(value)
        },
        getQuoteStatusOptions(quote){
            return quote.status === 'confirmed' ? ['canceled'] : this.quoteStatusOptions
        },
        isQuoteStatusDropdownDisabled(quote){
            return this.quoteStatusUpdatingId === quote.id
                || ['draft', 'canceled'].includes(quote.status)
        },
        async changeQuoteStatus(quote, status){
            if (this.isQuoteStatusDropdownDisabled(quote) || quote.status === status) return

            const previousStatus = quote.status
            this.quoteStatusUpdatingId = quote.id
            this.$set(quote, 'status', status)

            try{
                await helper.customPatch(
                    `${process.env.VUE_APP_API}/quotes/${quote.id}`,
                    { status },
                    { showToast: true }
                )
            }catch(error){
                this.$set(quote, 'status', previousStatus)
            }finally{
                await this.getQuote(quote.id)
                this.quoteStatusUpdatingId = null
            }
        },
        async getQuoteHistory(id){
            try{
                this.loadingHistory = true
                const response = await helper.customGet(`${process.env.VUE_APP_API}/quotes/${id}/history`)
                this.history = response.data.data || response.data
                this.historyPagination = {
                    count: response.data.count || 0,
                    per_page: response.data.per_page || 10,
                    current_page: response.data.current_page || 1,
                    total_pages: response.data.total_pages || 1
                }
                this.loadingHistory = false
            }catch(error){
                alert('Error fetching history:', error)
                this.loadingHistory = false
            }
        },
        async changeHistoryPage(page){
            if(page < 1 || page > this.historyPagination.total_pages) return;
            
            try{
                this.loadingHistory = true
                const response = await helper.customGet(`${process.env.VUE_APP_API}/quotes/${this.quote.id}/history?page=${page}&per_page=${this.historyPagination.per_page}`)
                this.history = response.data.data || response.data
                this.historyPagination = {
                    count: response.data.count || 0,
                    per_page: response.data.per_page || 10,
                    current_page: response.data.current_page || 1,
                    total_pages: response.data.total_pages || 1
                }
                this.loadingHistory = false
            }catch(error){
                alert('Error fetching history page:', error)
                this.loadingHistory = false
            }
        },
        editRow(){
            // this.clearForm()
            this.editModalisOpen = true
            this.quote.date = moment(this.quote.date).format('YYYY-MM-DD')
        },
        clearForm(){
            this.quote = {
                id : null,
                reference : '',
                client_id : null,
                client : {},
                date : '',
                total : 0,
                products : []
            }
        },
        async deleteRow(){
            this.$swal.fire({
            title: `${this.$t('are_you_sure')}?`,
            text : this.$t('you_cant_revert'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: this.$t('yes_delete_it')
            }).then(async (result) => {
                if (result.isConfirmed) {
                    await helper.customDelete(`${process.env.VUE_APP_API}/quotes/${this.quote.id}`)
                    this.$toast.open({
                        message: this.$t('item_moved_to_trash'),
                        type: 'success',
                        position: 'bottom'
                    });
                    this.$router.push({path : '/quote'})
                }
            })
        },
        async quoteAccepted(){
            this.quote.quote_id = this.quote.id
            this.quote.total = this.quote.products && this.quote.products.length ? this.quote.products.map(o => o.total).reduce((a, c) => { return a + c }) : 0;
            this.createOrderModalState = !this.createOrderModalState
        },
        async transferInvoice(){
            this.quote.quote_id = this.quote.id
            this.quote.total = this.quote.products && this.quote.products.length ? this.quote.products.map(o => o.total).reduce((a, c) => { return a + c }) : 0;
            this.createInvoiceModalState = !this.createInvoiceModalState
          }, 
        async transferDelivery(){
            this.quote.quote_id = this.quote.id
            this.quote.total = this.quote.products && this.quote.products.length ? this.quote.products.map(o => o.total).reduce((a, c) => { return a + c }) : 0;
            this.createDeliveryModalState = !this.createDeliveryModalState

        },
        createInvoiceModal(data){
            this.createInvoiceModalState = data
        },
        createDeliveryModalStatus(data){
            this.createDeliveryModalState = data
        },
        createOrderModalStatus(data){
            this.createOrderModalState = data
        },
        formatDate(date) { return helper.formatDate(date) },
        formatMoney(number) { return helper.formatMoney(number);},
        formatReference(number,id) { return helper.formatReference(number,id);},
        openSendEmailModal(){
            this.emailForm.email = this?.quote?.client?.email || ''
            this.sendEmailModal.show()
        },
        async sendEmail(){
            try{
                this.loading = true
                await helper.customPost(`${process.env.VUE_APP_API}/mails`, {
                    email: this.emailForm.email,
                    document_id: this.quote.id,
                    document_type: 'quote'
                })
                
                this.sendEmailModal.hide()
                this.$toast.open({
                    message: this.$t('email_sent_successfully'),
                    type: 'success',
                    position: 'bottom'
                })
                this.loading = false
            }catch(error){
                this.loading = false
                this.$toast.open({
                    message: error.response?.data?.message || this.$t('error_sending_email'),
                    type: 'error',
                    position: 'bottom'
                });
            }
        },
        async sendEmailFromPreview(payload){
            try{
                this.loading = true
                await helper.customPost(`${process.env.VUE_APP_API}/mails`, {
                    email: payload.email,
                    subject: payload.subject,
                    message: payload.message,
                    document_id: this.quote.id,
                    document_type: 'quote',
                    pdf_base64: payload.pdf_base64,
                    filename: payload.filename
                })
                
                // this.invoiceModalisOpen = false
                // this.showSendForm = false
                this.emailSent = true
                // this.$toast.open({
                //     message: this.$t('email_sent_successfully'),
                //     type: 'success',
                //     position: 'bottom'
                // })
                this.loading = false
            }catch(error){
                this.loading = false
                this.$toast.open({
                    message: error.response?.data?.message || this.$t('error_sending_email'),
                    type: 'error',
                    position: 'bottom'
                });
            }
        },
        toggleReorder() {
            if (!this.reorderMode) {
                // Enter reorder mode: save original state
                this.originalProducts = JSON.parse(JSON.stringify(this.quote.products));
                this.reorderMode = true;
            } else {
                // Cancel reorder mode: revert changes
                this.quote.products = this.originalProducts;
                this.reorderMode = false;
            }
        },
        moveUp(index) {
            if (index > 0) {
                const item = this.quote.products.splice(index, 1)[0];
                this.quote.products.splice(index - 1, 0, item);
            }
        },
        moveDown(index) {
            if (index < this.quote.products.length - 1) {
                const item = this.quote.products.splice(index, 1)[0];
                this.quote.products.splice(index + 1, 0, item);
            }
        },
        async saveOrder() {
            try {
                this.loading = true;
                
                // Prepare payload for editQuote
                // We need to strip fields that might cause issues if the backend doesn't expect them or if they are read-only
                // The editQuote endpoint expects 'products' array with specific fields.
                // It's safer to map to the exact expected structure.
                
                const formattedProducts = this.quote.products.map(p => ({
                    id: p.id,
                    inventory_id: p.inventory_id,
                    product_id: p.product_id,
                    product_label: p.product_label,
                    quantity: p.quantity,
                    price: p.price,
                    discount: p.discount,
                    unity: p.unity,
                    purchase_price: p.purchase_price,
                    vat: p.vat,
                    total: p.total,
                    sub_total: p.sub_total,
                    custom_product: p.custom_product
                }));

                await helper.customPatch(`${process.env.VUE_APP_API}/quotes/${this.quote.id}`, {
                    id: this.quote.id,
                    products: formattedProducts
                });

                this.reorderMode = false;
                this.$toast.open({
                    message: this.$t('saved_successfully'),
                    type: 'success',
                    position: 'bottom'
                });
                
                // Refresh quote to get fresh data
                await this.getQuote(this.quote.id);
                
            } catch (error) {
                this.loading = false;
                this.$toast.open({
                    message: error.response?.data?.message || 'Error saving order',
                    type: 'error',
                    position: 'bottom'
                });
            }
        },
        async duplicateRow(){
            try {
                // Copy the current quote
                let copiedQuote = JSON.parse(JSON.stringify(this.quote));
                
                // Reset IDs and date
                copiedQuote.id = null;
                copiedQuote.reference = '';
                copiedQuote.date = moment().format('YYYY-MM-DD');
                copiedQuote.status = 'draft';
                
                if(copiedQuote.products && copiedQuote.products.length){
                    copiedQuote.products.forEach(p => {
                        p.id = null;
                        p.quote_id = null;
                    });
                } else {
                    copiedQuote.products = [{
                        label: '',
                        id: null,
                        quantity: 0,
                        price: 0,
                        discount: 0,
                        vat: this.business.vat,
                        total: 0,
                        sub_total: 0,
                        custom_product: false
                    }];
                }
                
                this.quoteToDuplicate = copiedQuote;
                this.createModalisOpen = true;
            } catch (error) {
                helper.handleError(error);
            }
        }

  },
  computed : {
        business(){ return this.$store.getters.BUSINESS},
        helper(){ return helper},
        total(){ return this.quote.products.length ? this.quote.products.map(o => o.total).reduce((a, c) => { return a + c }) : 0},
        sub_total(){ return this.quote.products.length ? this.quote.products.map(o => o.sub_total).reduce((a, c) => { return a + c }) : 0}
  },
  watch : {
    '$route.params.id'(newId) {
      if (newId) {
        this.getQuote(newId)
      }
    },
    editModalisOpen(newValue) {
        if(newValue === false){
            this.getQuote(this.$route.params.id)
        }
    },
    step(newValue) {
        if(newValue === 'history' && this.quote.id){
            this.getQuoteHistory(this.quote.id)
        }
    }
  }
}
</script>

<style scoped>
.quote-product-rich-text::v-deep > :last-child {
    margin-bottom: 0 !important;
}
</style>
